import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Input,
  Table,
  Row,
  Col,
} from "reactstrap";
import api from "../../api";
import StatsModal from "../Common/StatsModal";

const TradeAccordion = ({
  profile,
  profileItems,
  myItems,
  myCurrency,
  showPopupMessage,
}) => {
  const [open, setOpen] = useState("1");

  // Pokémon Selections
  const [selectedMyPokemon, setSelectedMyPokemon] = useState([]);
  const [selectedProfilePokemon, setSelectedProfilePokemon] = useState([]);
  const [myPokemonSearch, setMyPokemonSearch] = useState("");
  const [profilePokemonSearch, setProfilePokemonSearch] = useState("");

  // Item Selections
  const [selectedMyItems, setSelectedMyItems] = useState([]);
  const [selectedProfileItems, setSelectedProfileItems] = useState([]);
  const [myItemAmounts, setMyItemAmounts] = useState({});
  const [profileItemAmounts, setProfileItemAmounts] = useState({});
  const [myItemSearch, setMyItemSearch] = useState("");
  const [profileItemSearch, setProfileItemSearch] = useState("");

  // Currency
  const [moneyOffer, setMoneyOffer] = useState(0);
  const [coinsOffer, setCoinsOffer] = useState(0);
  const [moneyRequest, setMoneyRequest] = useState(0);
  const [coinsRequest, setCoinsRequest] = useState(0);
  const [myPokemonTypeFilter, setMyPokemonTypeFilter] = useState("");
  const [profilePokemonTypeFilter, setProfilePokemonTypeFilter] = useState("");

  // Paginated Pokémon Lists
  const [myPaginatedPokemon, setMyPaginatedPokemon] = useState([]);
  const [profilePaginatedPokemon, setProfilePaginatedPokemon] = useState([]);
  const [myPage, setMyPage] = useState(1);
  const [profilePage, setProfilePage] = useState(1);
  const [myTotalPages, setMyTotalPages] = useState(1);
  const [profileTotalPages, setProfileTotalPages] = useState(1);
  const [myLoading, setMyLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [myItemsPage, setMyItemsPage] = useState(1);
  const [profileItemsPage, setProfileItemsPage] = useState(1);
  const ITEMS_ROWS_PER_PAGE = 5;

  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);

  const getTradableItems = (data) => {
    const pickaxeItems = [
      "Normal Pickaxe",
      "Ruby Pickaxe",
      "Emerald Pickaxe",
      "Sapphire Pickaxe",
      "Golden Pickaxe",
      "Diamond Pickaxe",
    ];

    const fishingRods = ["Old Rod", "Good Rod", "Super Rod","Catcher Rod","Trainer Rod","Miner Rod","Sheet Music"];
    const untradableItems = [...pickaxeItems, ...fishingRods];

    return (Array.isArray(data) ? data : []).filter(
      (item) => item.amount > 0 && !untradableItems.includes(item.item_name),
    );
  };

  const myTradableItems = getTradableItems(myItems).filter((item) =>
    String(item?.item_name || "")
      .toLowerCase()
      .includes(myItemSearch.toLowerCase()),
  );
  const profileTradableItems = getTradableItems(profileItems).filter((item) =>
    String(item?.item_name || "")
      .toLowerCase()
      .includes(profileItemSearch.toLowerCase()),
  );
  const myItemsTotalPages = Math.max(
    1,
    Math.ceil(myTradableItems.length / ITEMS_ROWS_PER_PAGE),
  );
  const profileItemsTotalPages = Math.max(
    1,
    Math.ceil(profileTradableItems.length / ITEMS_ROWS_PER_PAGE),
  );

  useEffect(() => {
    setMyItemsPage((p) => Math.min(Math.max(1, p), myItemsTotalPages));
  }, [myItemsTotalPages]);

  useEffect(() => {
    setProfileItemsPage((p) => Math.min(Math.max(1, p), profileItemsTotalPages));
  }, [profileItemsTotalPages]);

  useEffect(() => {
    setMyItemsPage(1);
  }, [myItemSearch]);

  useEffect(() => {
    setProfileItemsPage(1);
  }, [profileItemSearch]);

  const fetchMyPokemon = (page = 1) => {
    setMyLoading(true);
    api
      .get("/v1/pokemon", {
        params: {
          page,
          limit: rowsPerPage,
          type: myPokemonTypeFilter || undefined,
        },
        withCredentials: true,
      })
      .then((res) => {
        // Filter out Pokémon with a slot greater than 0
        const filteredPokemon = res.data.data.filter(
          (pokemon) => pokemon.slot <= 0,
        );
        setMyPaginatedPokemon(filteredPokemon);
        // Use the overall total from the API response to calculate total pages
        setMyTotalPages(Math.ceil(res.data.total / rowsPerPage));
        setMyPage(res.data.page);
        setMyLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setMyLoading(false);
      });
  };

  const fetchProfilePokemon = (page = 1) => {
    setProfileLoading(true);
    api
      .get("/v1/pokemon/box-view", {
        params: {
          username: profile.username,
          page,
          limit: rowsPerPage,
          type: profilePokemonTypeFilter || undefined,
        },
      })
      .then((res) => {
        // Filter out Pokémon with a slot greater than 0
        const filteredPokemon = res.data.data.filter(
          (pokemon) => pokemon.slot <= 0,
        );
        setProfilePaginatedPokemon(filteredPokemon);
        // Use the overall total from the API response to calculate total pages
        setProfileTotalPages(Math.ceil(res.data.total / rowsPerPage));
        setProfilePage(res.data.page);
        setProfileLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setProfileLoading(false);
      });
  };

  const searchMyPokemon = (query) => {
    if (!query) return fetchMyPokemon(myPage);
    setMyLoading(true);
    api
      .get("/v1/pokemon/search", { params: { query }, withCredentials: true })
      .then((res) => {
        setMyPaginatedPokemon(res.data);
        setMyTotalPages(1);
        setMyPage(1);
        setMyLoading(false);
      });
  };

  const searchProfilePokemon = (query) => {
    if (!query) return fetchProfilePokemon(profilePage);
    setProfileLoading(true);
    api
      .get("/v1/pokemon/box-search", {
        params: { username: profile.username, query },
      })
      .then((res) => {
        setProfilePaginatedPokemon(res.data);
        setProfileTotalPages(1);
        setProfilePage(1);
        setProfileLoading(false);
      });
  };

  useEffect(() => {
    fetchMyPokemon(myPage);
  }, [myPage, myPokemonTypeFilter]);

  useEffect(() => {
    fetchProfilePokemon(profilePage);
  }, [profilePage, profilePokemonTypeFilter]);

  const toggle = (id) => setOpen(open === id ? "" : id);

  const toggleStatsModal = () => setStatsModalOpen((prev) => !prev);

  const handleOpenStats = async (pokemon) => {
    if (!pokemon?.id) return;
    setStatsModalOpen(true);
    setStatsLoading(true);
    setStatsData(null);
    try {
      const res = await api.get(`/v1/pokemon/${pokemon.id}`, {
        withCredentials: true,
      });
      setStatsData(res.data);
    } catch (err) {
      console.error(err);
      setStatsData({ error: "Failed to load Pokémon stats." });
    } finally {
      setStatsLoading(false);
    }
  };

  // Handle Create Trade Submission
  const handleCreateTrade = async () => {
    const isPokemonEmpty =
      selectedMyPokemon.length === 0 && selectedProfilePokemon.length === 0;
    const isItemsEmpty =
      selectedMyItems.length === 0 && selectedProfileItems.length === 0;
    const isCurrencyEmpty =
      moneyOffer === 0 &&
      moneyRequest === 0 &&
      coinsOffer === 0 &&
      coinsRequest === 0;

    if (isPokemonEmpty && isItemsEmpty && isCurrencyEmpty) {
      showPopupMessage(
        "You must include at least one Pokémon, item, or currency in the trade.",
        "",
        "#f44336",
      );
      return;
    }
    const payload = {
      user_to: profile.id,
      tradetime: Math.floor(Date.now() / 1000),
      money_from: moneyOffer,
      money_to: moneyRequest,
      coins_from: coinsOffer,
      coins_to: coinsRequest,
      trade_details: {
        pokemon_offer: selectedMyPokemon.map((p) => ({ id: p.id })),
        pokemon_receive: selectedProfilePokemon.map((p) => ({ id: p.id })),
        items_offer: selectedMyItems.map((item) => ({
          item: item.item,
          amount: Number(myItemAmounts[item.item] || 1),
        })),
        items_receive: selectedProfileItems.map((item) => ({
          item: item.item,
          amount: Number(profileItemAmounts[item.item] || 1),
        })),
      },
    };

    try {
      await api.post("/v1/create-trade", payload, { withCredentials: true });
      showPopupMessage("Trade created successfully!", "", "#4CAF50");
    } catch (err) {
      console.error(err);
      showPopupMessage("Failed to create trade.", "", "#f44336");
    }
  };

  // Render Items Table (unchanged)
  const renderItemsTable = (
    filteredItems,
    selected,
    setSelected,
    amounts,
    setAmounts,
    search,
    setSearch,
    page,
    setPage,
    totalPages,
  ) => {
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const start = (safePage - 1) * ITEMS_ROWS_PER_PAGE;
    const pageItems = filteredItems.slice(start, start + ITEMS_ROWS_PER_PAGE);

    return (
      <>
        <Row className="mb-2 trade-filter-row">
          <Col xs="8">
            <Input
              type="text"
              placeholder="Search Items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="trade-search-input"
            />
          </Col>
        </Row>
        <Table responsive striped className="trade-selection-table">
          <thead>
            <tr>
              <th>Select</th>
              <th>Item</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((item) => (
              <tr key={item.item}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.some((i) => i.item === item.item)}
                    onChange={() =>
                      setSelected((prev) =>
                        prev.some((i) => i.item === item.item)
                          ? prev.filter((i) => i.item !== item.item)
                          : [...prev, item],
                      )
                    }
                  />
                </td>
                <td>
                  <img
                    src={item.item_image}
                    alt={item.item_name}
                    style={{ width: "24px", marginRight: "8px" }}
                  />
                  {item.item_name}
                </td>
                <td>
                  <Input
                    type="number"
                    min="1"
                    value={amounts[item.item] || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setAmounts({ ...amounts, [item.item]: value });
                      if (
                        Number(value) > 0 &&
                        !selected.some((i) => i.item === item.item)
                      ) {
                        setSelected([...selected, item]);
                      }
                      if (
                        (!value || Number(value) === 0) &&
                        selected.some((i) => i.item === item.item)
                      ) {
                        setSelected(selected.filter((i) => i.item !== item.item));
                      }
                    }}
                    style={{
                      width: "80px",
                      display: "inline-block",
                      marginRight: "5px",
                    }}
                  />
                  <span style={{ color: "#999" }}>/ {item.amount}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <div className="d-flex justify-content-between align-items-center trade-pagination">
          <Button
            color="secondary"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={safePage <= 1}
          >
            Prev
          </Button>
          <span>
            Page {safePage} of {totalPages}
          </span>
          <Button
            color="secondary"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={safePage >= totalPages}
          >
            Next
          </Button>
        </div>
      </>
    );
  };

  // Render Paginated Pokémon Table including the checkbox input
  const renderPaginatedPokemonTable = (
    data,
    selected,
    setSelected,
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    currentPage,
    setCurrentPage,
    totalPages,
    loading,
    searchFunction,
  ) => {
    return (
      <>
        <Row className="mb-2 trade-filter-row">
          <Col xs="5">
            <Input
              type="text"
              placeholder="Search Pokémon"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                searchFunction(e.target.value);
              }}
              className="trade-search-input"
            />
          </Col>
          <Col xs="4">
            <Input
              type="select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="trade-filter-select"
            >
              <option value="">All Types</option>
              <option value="Diamond">Diamond</option>
              <option value="Emerald">Emerald</option>
              <option value="Golden">Golden</option>
              <option value="Ruby">Ruby</option>
              <option value="Sapphire">Sapphire</option>
              <option value="Shadow">Shadow</option>
              <option value="Shiny">Shiny</option>
            </Input>
          </Col>
        </Row>

        <Table responsive striped>
          <thead>
            <tr>
              <th>Select</th>
              <th>Pokémon</th>
              <th>Name</th>
              <th>Level</th>
              <th>Gender</th>
              <th>Stats</th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              data.map((poke) => (
                <tr key={poke.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.some((p) => p.id === poke.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log("Toggling selection for:", poke.id);
                        const alreadySelected = selected.some(
                          (p) => p.id === poke.id,
                        );
                        setSelected(
                          alreadySelected
                            ? selected.filter((p) => p.id !== poke.id)
                            : [...selected, poke],
                        );
                      }}
                    />
                  </td>
                  <td>
                    <img
                      src={`/sprites/${poke.name}.png`}
                      alt={poke.name}
                      style={{ width: "60px" }}
                    />
                  </td>
                  <td>{poke.nickname || poke.name}</td>
                  <td>{Math.floor(Math.pow(poke.totalexp, 1 / 3))}</td>
                  <td>{poke.gender.toUpperCase()}</td>
                  <td>
                    <Button
                      size="sm"
                      color="info"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenStats(poke);
                      }}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            {loading && (
              <tr>
                <td colSpan="6" className="text-center">
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        <div className="d-flex justify-content-between align-items-center">
          <Button
            color="secondary"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </Button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <Button
            color="secondary"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="trade-accordion trade-accordion--profile">
      <Accordion open={open} toggle={toggle} className="trade-accordion__shell">
        {/* Pokémon Tab */}
        <AccordionItem>
          <AccordionHeader targetId="1">Pokémon</AccordionHeader>
          <AccordionBody accordionId="1">
            <div className="trade-offer-grid">
              <div className="trade-offer-panel">
                <h6 className="trade-offer-title">Your Pokémon</h6>
                {renderPaginatedPokemonTable(
                  myPaginatedPokemon,
                  selectedMyPokemon,
                  setSelectedMyPokemon,
                  myPokemonSearch,
                  setMyPokemonSearch,
                  myPokemonTypeFilter,
                  setMyPokemonTypeFilter,
                  myPage,
                  setMyPage,
                  myTotalPages,
                  myLoading,
                  searchMyPokemon,
                )}
              </div>
              <div className="trade-offer-panel">
                <h6 className="trade-offer-title">Their Pokémon</h6>
                {renderPaginatedPokemonTable(
                  profilePaginatedPokemon,
                  selectedProfilePokemon,
                  setSelectedProfilePokemon,
                  profilePokemonSearch,
                  setProfilePokemonSearch,
                  profilePokemonTypeFilter,
                  setProfilePokemonTypeFilter,
                  profilePage,
                  setProfilePage,
                  profileTotalPages,
                  profileLoading,
                  searchProfilePokemon,
                )}
              </div>
            </div>
          </AccordionBody>
        </AccordionItem>

        {/* Items Tab */}
        <AccordionItem>
          <AccordionHeader targetId="2">Items</AccordionHeader>
          <AccordionBody accordionId="2">
            <div className="trade-offer-grid">
              <div className="trade-offer-panel">
                <h6 className="trade-offer-title">Your Items</h6>
                {renderItemsTable(
                  myTradableItems,
                  selectedMyItems,
                  setSelectedMyItems,
                  myItemAmounts,
                  setMyItemAmounts,
                  myItemSearch,
                  setMyItemSearch,
                  myItemsPage,
                  setMyItemsPage,
                  myItemsTotalPages,
                )}
              </div>
              <div className="trade-offer-panel">
                <h6 className="trade-offer-title">Their Items</h6>
                {renderItemsTable(
                  profileTradableItems,
                  selectedProfileItems,
                  setSelectedProfileItems,
                  profileItemAmounts,
                  setProfileItemAmounts,
                  profileItemSearch,
                  setProfileItemSearch,
                  profileItemsPage,
                  setProfileItemsPage,
                  profileItemsTotalPages,
                )}
              </div>
            </div>
          </AccordionBody>
        </AccordionItem>

        {/* Currency Tab */}
        <AccordionItem>
          <AccordionHeader targetId="3">Currency</AccordionHeader>
          <AccordionBody accordionId="3">
            <div className="trade-currency-grid">
              <Row className="mb-3">
                <Col xs="6">
                  <div className="trade-currency-label">
                    <img
                      src="/items/money.png"
                      alt="money"
                      style={{ width: "24px", marginRight: "8px" }}
                    />
                    Money to Offer
                  </div>
                  <Input
                    type="number"
                    value={moneyOffer}
                    onChange={(e) => setMoneyOffer(Number(e.target.value))}
                    min={0}
                    max={myCurrency?.money || undefined}
                  />
                </Col>
                <Col xs="6">
                  <div className="trade-currency-label">
                    <img
                      src="/items/coin.png"
                      alt="coin"
                      style={{ width: "24px", marginRight: "8px" }}
                    />
                    Coins to Offer
                  </div>
                  <Input
                    type="number"
                    value={coinsOffer}
                    onChange={(e) => setCoinsOffer(Number(e.target.value))}
                    min={0}
                    max={myCurrency?.coins || undefined}
                  />
                </Col>
              </Row>

              <Row>
                <Col xs="6" className="mt-3">
                  <div className="trade-currency-label">
                    <img
                      src="/items/money.png"
                      alt="money"
                      style={{ width: "24px", marginRight: "8px" }}
                    />
                    Money to Receive
                  </div>
                  <Input
                    type="number"
                    placeholder="Money to Receive"
                    value={moneyRequest}
                    onChange={(e) => setMoneyRequest(Number(e.target.value))}
                    min={0}
                    max={profile?.money || undefined}
                  />
                </Col>
                <Col xs="6" className="mt-3">
                  <div className="trade-currency-label">
                    <img
                      src="/items/coin.png"
                      alt="coin"
                      style={{ width: "24px", marginRight: "8px" }}
                    />
                    Coins to Receive
                  </div>
                  <Input
                    type="number"
                    placeholder="Coins to Receive"
                    value={coinsRequest}
                    onChange={(e) => setCoinsRequest(Number(e.target.value))}
                    min={0}
                    max={profile?.coins || undefined}
                  />
                </Col>
              </Row>

              <Row className="mt-4">
                <Col xs="6">
                  <div className="trade-currency-label">
                    Your Available Funds:
                  </div>
                  <div className="trade-currency-balance">
                    <img
                      src="/items/money.png"
                      alt="money"
                      style={{ width: "20px", marginRight: "4px" }}
                    />
                    {myCurrency?.money?.toLocaleString() || 0}
                  </div>
                  <div className="trade-currency-balance">
                    <img
                      src="/items/coin.png"
                      alt="coin"
                      style={{ width: "20px", marginRight: "4px" }}
                    />
                    {myCurrency?.coins?.toLocaleString() || 0}
                  </div>
                </Col>
                <Col xs="6">
                  <div className="trade-currency-label">
                    Their Available Funds:
                  </div>
                  <div className="trade-currency-balance">
                    <img
                      src="/items/money.png"
                      alt="money"
                      style={{ width: "20px", marginRight: "4px" }}
                    />
                    {profile?.money?.toLocaleString() || 0}
                  </div>
                  <div className="trade-currency-balance">
                    <img
                      src="/items/coin.png"
                      alt="coin"
                      style={{ width: "20px", marginRight: "4px" }}
                    />
                    {profile?.coins?.toLocaleString() || 0}
                  </div>
                </Col>
              </Row>
            </div>
          </AccordionBody>
        </AccordionItem>
        <div className="trade-action-bar">
          <Button
            color="primary"
            onClick={handleCreateTrade}
            className="trade-action-btn"
          >
            Create Trade
          </Button>
        </div>
      </Accordion>

      <StatsModal
        isOpen={statsModalOpen}
        toggle={toggleStatsModal}
        loading={statsLoading}
        data={statsData}
      />
    </div>
  );
};

export default TradeAccordion;
